import { css, styled } from 'styled-components';
import cssSystem from '@styled-system/css';

import { Box, Card, Flex, Heading, Text, List, THEME_UTILITIES, useBrand } from '@cof/plastic-components';
import PropTypes from 'prop-types';
import { withText } from '../../text/textStore';
import FormErrors from '../FormErrors';
import partners from '../../partners';
import isMobile from 'is-mobile';
import config from '@cof/ukweb-config/lib/clientConfig';
import { isInstantIssuanceBrand } from '../../utilities/utils';
import Reason from './Reason';
import ASOSCard from './assets/ASOSCard.png';
import CapitalOneCard from './assets/CapitalOneCard.png';
import LittlewoodsCard from './assets/LittlewoodsCard.png';
import LumaCard from './assets/LumaCard.png';
import VeryCard from './assets/VeryCard.png';

const cardImageMap = {
    [partners.CAPITAL_ONE]: CapitalOneCard,
    [partners.LITTLEWOODS]: LittlewoodsCard,
    [partners.LUMA]: LumaCard,
    [partners.VERY]: VeryCard,
    [partners.ASOS]: ASOSCard
};

const FormCard = styled(Card)`
    box-shadow: unset;
    padding: 0;
`;

const GradientText = styled.span`
    background: linear-gradient(96.87deg, #ff8791 22.28%, #ffe433 97.69%);
    -webkit-text-fill-color: transparent;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-background-clip: text;
`;

const { useColor } = THEME_UTILITIES;

const InnerCardContainer = styled(Box)`
    ${({ theme }) => css`
        height: 16rem;
        padding: 4rem 10.5rem 0 10.5rem;
        ${cssSystem({ maxWidth: ['34.5rem', null, '67.4rem'] })}
        ${theme.breakpoints.mq.mobileMax} {
            padding: ${theme.space.sm};
            height: 9.7rem;
            margin: auto;
        }
    `}
`;

const CardContainer = styled(Box)`
    ${({ theme }) => css`
        ${theme.components.CardContainer}
    `};
    border-radius: 0.8rem 0.8rem 0 0;
`;

const StyledCardImage = styled('img')`
    ${({ theme }) => css`
        ${theme.breakpoints.mq.tabletMin} {
            width: 464px;
            height: 121px;
        }
    `}
`;

const SimpleHeader = ({ Text: text, isEdit = false }) => {
    const headingLine1Colors = useColor({
        [partners.ASOS]: 'ASOS.purple350',
        [partners.CAPITAL_ONE]: 'global.white',
        [partners.LITTLEWOODS]: 'littlewoods.purple550',
        [partners.LUMA]: 'luma.purple500'
    });

    const iconColors = useColor({
        [partners.ASOS]: 'ASOS.purple350',
        [partners.CAPITAL_ONE]: 'capitalOne.teal450',
        [partners.LITTLEWOODS]: 'littlewoods.purple550',
        [partners.LUMA]: 'luma.purple500',
        [partners.VERY]: 'very.pink500'
    });

    const brand = useBrand();

    const isMobileDevice = isMobile({ tablet: true, featureDetect: false });

    const isInstantIssuance =
        config.get('features.instantIssuance') && isInstantIssuanceBrand(brand) && isMobileDevice && !isEdit;

    return (
        <FormCard data-qa-id="simple-header" backgroundColor="transparent">
            <Flex flexDirection={'column'} maxWidth="100%">
                <Flex flexDirection={'column'} pb={['md', null, 'lg']} px={['xs', null, 'lg']} textAlign={'left'}>
                    <Heading
                        as="h1"
                        textAlign="left"
                        color={headingLine1Colors}
                        pb={'xs'}
                        data-qa-id="heading"
                        fontSize={['headingLargeMobile', null, 'headingMediumDesktop']}
                        data-qm-allow
                    >
                        {text({ path: 'Title1' })} <br aria-hidden={true} />
                        {brand === 'capitalOne' ? (
                            <GradientText data-qa-id="gradient-title2" data-qm-allow>
                                {text({ path: 'Title2' })}
                            </GradientText>
                        ) : (
                            <Text
                                fontSize={['inherit', 'inherit']}
                                fontWeight={'medium'}
                                data-qa-id="block-title2"
                                data-qm-allow
                            >
                                {text({ path: 'Title2' })}
                            </Text>
                        )}
                    </Heading>
                    <List paddingLeft={[0, 0, 0]}>
                        <Reason
                            iconColors={iconColors}
                            reasonIndex={1}
                            text={text}
                            isInstantIssuance={isInstantIssuance}
                        />
                        {isInstantIssuance ? (
                            <Reason iconColors={iconColors} isInstantIssuance text={text} reasonIndex={2} />
                        ) : (
                            <Reason iconColors={iconColors} reasonIndex={3} text={text} />
                        )}
                    </List>
                </Flex>
                <FormErrors />
                <CardContainer>
                    <InnerCardContainer>
                        <StyledCardImage
                            src={cardImageMap[brand]}
                            alt=""
                            data-qa-id={`${brand}-card-image`}
                            width="313"
                            height="82"
                        />
                    </InnerCardContainer>
                </CardContainer>
            </Flex>
        </FormCard>
    );
};

SimpleHeader.propTypes = {
    Text: PropTypes.func.isRequired,
    isEdit: PropTypes.bool
};

const WrappedSimpleHeader = withText('SimpleHeader')(SimpleHeader);

WrappedSimpleHeader.displayName = 'SimpleHeader';

export default WrappedSimpleHeader;
